/**
 * 이미지 업로드 공통 상수 및 검증 헬퍼.
 *
 * 에디터(TinyMCE, TipTap)와 패널(DataTableWriterPanel, AiWriterPanel) 전반에서
 * 동일한 정책으로 size/개수 제한을 검증하기 위해 사용한다.
 */

export const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_IMAGE_COUNT = 10;

/**
 * 바이트 수를 사람이 읽기 좋은 단위 문자열로 변환한다. (예: "12.3MB")
 * @param {number} bytes
 * @returns {string}
 */
export function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '0B';
  if (bytes < 1024) return `${bytes}B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)}KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)}MB`;
}

/**
 * File 객체의 size를 검증한다.
 * @param {File} file
 * @returns {{ valid: boolean, fileName: string, actualSize: string }}
 */
export function validateImageSize(file) {
  const fileName = file?.name || 'unknown';
  const size = file?.size || 0;
  return {
    valid: size <= MAX_IMAGE_SIZE,
    fileName,
    actualSize: formatSize(size)
  };
}

/**
 * base64 데이터 URL 문자열의 실제 바이트 크기를 추정한다.
 * 정확한 값은 아니지만(±몇 바이트), size 검증 용도로 충분하다.
 * @param {string} base64DataUrl - "data:image/...;base64,XXXX" 형식
 * @returns {number} 추정 바이트 수
 */
export function estimateBase64Size(base64DataUrl) {
  if (typeof base64DataUrl !== 'string') return 0;
  const commaIdx = base64DataUrl.indexOf(',');
  const payload = commaIdx >= 0 ? base64DataUrl.slice(commaIdx + 1) : base64DataUrl;
  const paddingMatch = payload.match(/=+$/);
  const padding = paddingMatch ? paddingMatch[0].length : 0;
  return Math.floor((payload.length * 3) / 4) - padding;
}

/**
 * 여러 File을 한 번에 검증한다.
 * @param {File[]} files
 * @returns {{ valid: File[], invalid: Array<{ file: File, actualSize: string }> }}
 */
export function partitionBySize(files) {
  const valid = [];
  const invalid = [];
  for (const file of files) {
    const result = validateImageSize(file);
    if (result.valid) {
      valid.push(file);
    } else {
      invalid.push({ file, actualSize: result.actualSize });
    }
  }
  return { valid, invalid };
}

/**
 * 다중 업로드 시 invalid 파일 목록을 토스트 메시지로 포맷한다.
 * @param {Array<{ file: File, actualSize: string }>} invalid
 * @returns {string}
 */
export function formatOversizedMessage(invalid) {
  if (!invalid?.length) return '';
  if (invalid.length === 1) {
    const { file, actualSize } = invalid[0];
    return `10MB 이하 이미지만 업로드 가능합니다.\n${file.name} (${actualSize})`;
  }
  const list = invalid.map(({ file, actualSize }) => `• ${file.name} (${actualSize})`).join('\n');
  return `다음 파일이 10MB를 초과합니다:\n${list}`;
}
